import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import { useQuery } from '../../../../hooks/routing/use-query';
import { useModal } from '../../../../hooks/use-modal';
import { csvImportParam, csvJobIdParam } from '../constants';

/** @typedef {import('../../data/types/import-type').ImportTypes} ImportTypes */

/**
 * @param {ImportTypes} importType
 * @returns {string}
 */
const createImportSearchPath = (importType) => `?${csvImportParam}=${importType}`;

/**
 * @param {ImportTypes} importType
 * @param {string} jobId
 * @returns {string}
 */
const createImportSummarySearchPath = (importType, jobId) => `${createImportSearchPath(importType)}&${csvJobIdParam}=${jobId}`;

/**
 * When the import is finished, csv-import.epic.js will be "notified" by pusher.
 * Then epic will check if the user is still in the context of the modal, i.e. importType===import.
 * This is mainly used to manage notifications, notifications allow you to open import result in any part of the application.
 * @param {ImportTypes} importType - The type of import to manage
 * @returns {{open: () => void; close: () => void; isOpen: boolean}}
 */
const useCsvImport = (importType) => {
  const { open: openFn, close: closeFn } = useModal();

  const history = useHistory();

  const open = useCallback(() => {
    history.push({ search: createImportSearchPath(importType) });
    openFn();
  }, [history, importType, openFn]);

  const close = useCallback(() => {
    history.push({ search: '' });
    closeFn();
  }, [closeFn, history]);

  const isOpen = useQuery().get(csvImportParam) === importType;

  return {
    open,
    close,
    isOpen,
  };
};

export { createImportSearchPath, createImportSummarySearchPath, useCsvImport };
